import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Button, Card, Dropdown, Empty, Input, InputNumber, Modal, Pagination, Popconfirm, Progress, Select, Space, Spin, Table, Tag, message } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { CloudOutlined, DeleteOutlined, DownloadOutlined, EditOutlined, EyeOutlined, FileTextOutlined, ReloadOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import apiClient from '../services/apiClient';
import { useAuth } from '../context/AuthContext';
import { useCloud } from '../context/CloudContext';
import { customerInitials, customerLogoCandidates, normalizeCustomerDomain } from '../utils/customerBranding';
import ReportEmailRecipientsCard from './ReportEmailRecipientsCard';
import './ConnectedAccounts.css';

interface ReportSummary {
  monthlyAverageCost?: number;
  netMonthlySavings?: number;
  customerLabel?: string;
  scopeLabel?: string;
  accountName?: string;
  accountAlias?: string;
  providerAccountId?: string;
  customerDomain?: string;
  customerLogoUrl?: string;
}

interface CloudAccount {
  id: string;
  tenantId: string;
  provider: 'aws' | 'azure' | 'gcp';
  name: string;
  providerAccountId?: string;
  accountId?: string;
  subscriptionId?: string;
  region?: string;
  status?: string;
  connectionMethod?: string;
  createdByEmail?: string;
  updatedAt?: string;
  lastConnectedAt?: string;
  lastReportCreatedAt?: string;
  lastReportRunId?: string;
  lastReportSummary?: ReportSummary;
  metadata?: {
    lastMonthlyAverageCost?: number;
    lastNetMonthlySavings?: number;
    lastRefreshAt?: string;
    lastRefreshStartedAt?: string;
    lastRefreshStatus?: string;
    lastRefreshScope?: 'quick' | 'inventory' | 'full';
    costsUpdatedAt?: string;
    inventoryUpdatedAt?: string;
    analysisUpdatedAt?: string;
    enabledRegionCount?: number;
    regionWithResourcesCount?: number;
    regionalCollectionStatus?: 'complete' | 'partial';
    lastRefreshError?: string | null;
    lastRefreshReportId?: string;
    lastRefreshProgress?: {
      percent?: number;
      stage?: string;
      message?: string;
      projectsCompleted?: number;
      projectsTotal?: number;
      estimatedSecondsRemaining?: number | null;
      reportId?: string;
      updatedAt?: string;
    };
    accountAlias?: string;
    accountDisplayName?: string;
    reportCount?: number;
    customerName?: string;
    customerDomain?: string;
    customerLogoUrl?: string;
    billingAccountId?: string;
    billingExportProjectId?: string;
    billingExportDataset?: string;
    billingExportTable?: string;
    workloadIdentityProvider?: string;
    serviceAccountEmail?: string;
    billingCorrectionPercent?: string;
  };
  reportCount?: number;
}

interface ReportRun {
  id: string;
  tenantId: string;
  cloudAccountId?: string;
  provider?: string;
  accountId?: string;
  status?: string;
  reportType?: string;
  createdAt?: string;
  createdByEmail?: string;
  summary?: ReportSummary;
  snapshot?: Record<string, any>;
}

interface ReportAccountGroup {
  id: string;
  displayName: string;
  identifier?: string;
  provider?: string;
  customerDomain?: string;
  customerLogoUrl?: string;
  latestReport: ReportRun;
  latestReportDate?: string;
  reportCount: number;
  reports: ReportRun[];
  monthlyAverageCost?: number;
  netMonthlySavings?: number;
  createdByEmail?: string;
}

interface CustomerAccountGroup {
  id: string;
  customerName: string;
  customerDomain?: string;
  accounts: CloudAccount[];
  providers: string[];
  latestUpdate?: string;
  monthlyAverageCost: number;
}

interface PaginationState {
  currentToken?: string;
  nextToken?: string | null;
  previousTokens: Array<string | undefined>;
}

interface ListFilters {
  provider?: string;
  ownerEmail?: string;
  search?: string;
  dateFrom?: string;
  dateTo?: string;
}

const ACCOUNT_PAGE_SIZE = 100;
const REPORT_PAGE_SIZE = 50;

const formatElapsedTime = (startedAt?: string) => {
  const startedAtMs = Date.parse(startedAt || '');
  if (!Number.isFinite(startedAtMs)) return 'Processamento em andamento';
  const elapsedMinutes = Math.max(0, Math.floor((Date.now() - startedAtMs) / 60000));
  if (elapsedMinutes < 1) return 'Em processamento há menos de 1 min';
  if (elapsedMinutes < 60) return `Em processamento há ${elapsedMinutes} min`;
  const hours = Math.floor(elapsedMinutes / 60);
  const minutes = elapsedMinutes % 60;
  return `Em processamento há ${hours}h${minutes ? ` ${minutes}min` : ''}`;
};

const currencyFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2
});

const brlCurrencyFormatter = new Intl.NumberFormat('pt-BR', {
  style: 'currency',
  currency: 'BRL',
  minimumFractionDigits: 2
});

const currencyFormatterForProvider = (provider?: string) =>
  provider === 'azure' ? brlCurrencyFormatter : currencyFormatter;

const dateFormatter = (value?: string) => {
  if (!value) return '-';
  return new Date(value).toLocaleString('pt-BR');
};

const providerLabel = (provider?: string) => {
  if (provider === 'aws') return 'AWS';
  if (provider === 'azure') return 'Azure';
  if (provider === 'gcp') return 'GCP';
  return provider?.toUpperCase() || '-';
};

const hasMeaningfulAwsDashboardData = (data: any) => {
  const inventory = data?.inventory || {};
  const inventoryTotal = Object.values(inventory).reduce<number>((sum, value) => sum + Number(value || 0), 0);
  const detailTotal = [
    data?.ec2Details,
    data?.rdsDetails,
    data?.s3Details,
    data?.eksDetails,
    data?.idleResourcesDetails,
    data?.securityDetails,
    data?.topServices
  ].reduce((sum, value) => sum + (Array.isArray(value) ? value.length : 0), 0);

  return Boolean(
    Number(data?.overview?.monthlyAverageCost || 0) > 0 ||
    Number(data?.overview?.netCost || 0) > 0 ||
    inventoryTotal > 0 ||
    detailTotal > 0
  );
};

const ConnectedAccounts: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isPlatformAdmin = Boolean(user?.roles?.some(role => role.toLowerCase() === 'platform-admin'));
  const canManageReportRecipients = Boolean(
    user?.roles?.some(role => ['admin', 'commercial', 'platform-admin'].includes(role.toLowerCase()))
  );
  // Mesmo criterio de canManageCloudAccounts em routes/cloudAccounts.ts -
  // editar/remover uma conexao e acao de credencial, nao de leitura.
  const canManageCloudAccounts = canManageReportRecipients;
  // Mesmo criterio de canGenerateReports em routes/cloudAccounts.ts - o
  // perfil 'finops' de um cliente pode disparar atualizacao, 'viewer' so ve
  // os relatorios ja gerados por outra pessoa do tenant.
  const canGenerateReports = Boolean(
    user?.roles?.some(role => ['admin', 'commercial', 'platform-admin', 'finops'].includes(role.toLowerCase()))
  );
  const { setConnection } = useCloud();
  const [accounts, setAccounts] = useState<CloudAccount[]>([]);
  const [reports, setReports] = useState<ReportRun[]>([]);
  const [loadingAccounts, setLoadingAccounts] = useState(false);
  const [loadingReports, setLoadingReports] = useState(false);
  const [refreshingAccountId, setRefreshingAccountId] = useState<string | null>(null);
  const [exportingInventoryAccountId, setExportingInventoryAccountId] = useState<string | null>(null);
  const [savingAccountId, setSavingAccountId] = useState<string | null>(null);
  const [deletingAccountId, setDeletingAccountId] = useState<string | null>(null);
  const [editingAccount, setEditingAccount] = useState<CloudAccount | null>(null);
  const [editForm, setEditForm] = useState({
    name: '',
    region: '',
    customerName: '',
    customerDomain: '',
    billingAccountId: '',
    billingExportProjectId: '',
    billingExportDataset: '',
    billingExportTable: '',
    billingCorrectionPercent: ''
  });
  const [gcpTenantCorrectionPercent, setGcpTenantCorrectionPercent] = useState<string>('');
  const [loadingGcpCorrectionPercent, setLoadingGcpCorrectionPercent] = useState(false);
  const [savingGcpCorrectionPercent, setSavingGcpCorrectionPercent] = useState(false);
  const [logoIndexByAccount, setLogoIndexByAccount] = useState<Record<string, number>>({});
  const [openingReportId, setOpeningReportId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [accountFilters, setAccountFilters] = useState<ListFilters>({});
  const [reportFilters, setReportFilters] = useState<ListFilters>({});
  const [accountPagination, setAccountPagination] = useState<PaginationState>({ previousTokens: [], nextToken: null });
  const [reportPagination, setReportPagination] = useState<PaginationState>({ previousTokens: [], nextToken: null });
  const previouslyProcessingIds = useRef<Set<string>>(new Set());

  const loading = loadingAccounts || loadingReports;

  const buildParams = (filters: ListFilters, limit: number, token?: string) => {
    const params: Record<string, string | number> = { limit };
    Object.entries(filters).forEach(([key, value]) => {
      if (value) {
        params[key] = value;
      }
    });
    if (token) {
      params.nextToken = token;
    }
    return params;
  };

  const loadAccounts = useCallback(async (token?: string, previousTokens: Array<string | undefined> = []) => {
    setLoadingAccounts(true);
    setError(null);

    try {
      const accountsResponse = await apiClient.get('/cloud/accounts', {
        params: buildParams(accountFilters, ACCOUNT_PAGE_SIZE, token)
      });
      setAccounts(accountsResponse.data?.accounts || []);
      setAccountPagination({
        currentToken: token,
        nextToken: accountsResponse.data?.pagination?.nextToken || null,
        previousTokens
      });
    } catch (loadError) {
      console.error('Erro ao carregar contas e relatórios:', loadError);
      setError('Nao foi possivel carregar contas conectadas e historico de relatorios.');
    } finally {
      setLoadingAccounts(false);
    }
  }, [accountFilters]);

  const loadReports = useCallback(async (token?: string, previousTokens: Array<string | undefined> = []) => {
    setLoadingReports(true);
    setError(null);

    try {
      const reportsResponse = await apiClient.get('/reports/runs', {
        params: buildParams(reportFilters, REPORT_PAGE_SIZE, token)
      });
      setReports(reportsResponse.data?.reports || []);
      setReportPagination({
        currentToken: token,
        nextToken: reportsResponse.data?.pagination?.nextToken || null,
        previousTokens
      });
    } catch (loadError) {
      console.error('Erro ao carregar relatórios:', loadError);
      setError('Nao foi possivel carregar contas conectadas e historico de relatorios.');
    } finally {
      setLoadingReports(false);
    }
  }, [reportFilters]);

  const loadData = useCallback(async () => {
    await Promise.all([
      loadAccounts(undefined, []),
      loadReports(undefined, [])
    ]);
  }, [loadAccounts, loadReports]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const hasProcessingRefresh = accounts.some(account => account.metadata?.lastRefreshStatus === 'processing');
  useEffect(() => {
    const currentProcessing = new Set(
      accounts.filter(account => account.metadata?.lastRefreshStatus === 'processing').map(account => account.id)
    );
    accounts.forEach(account => {
      if (!previouslyProcessingIds.current.has(account.id) || currentProcessing.has(account.id)) return;
      if (account.metadata?.lastRefreshStatus === 'success') {
        message.success(`Coleta GCP concluída para ${account.name}. O novo relatório está disponível.`);
        const reportId = account.metadata.lastRefreshReportId || account.metadata.lastRefreshProgress?.reportId;
        if (account.provider === 'gcp' && reportId) {
          navigate(
            `/gcp-dashboard?cloudAccountId=${encodeURIComponent(account.id)}` +
            `&reportId=${encodeURIComponent(reportId)}`
          );
        }
      } else if (account.metadata?.lastRefreshStatus === 'failed') {
        message.error(account.metadata?.lastRefreshError || `A coleta GCP falhou para ${account.name}.`);
      }
    });
    previouslyProcessingIds.current = currentProcessing;
  }, [accounts, navigate]);

  useEffect(() => {
    if (!hasProcessingRefresh) return;
    const timer = window.setInterval(() => void loadData(), 10000);
    return () => window.clearInterval(timer);
  }, [hasProcessingRefresh, loadData]);

  const updateAccountFilter = (key: keyof ListFilters, value?: string) => {
    setAccountFilters(filters => ({ ...filters, [key]: value || undefined }));
  };

  const updateReportFilter = (key: keyof ListFilters, value?: string) => {
    setReportFilters(filters => ({ ...filters, [key]: value || undefined }));
  };

  const goToNextAccountsPage = () => {
    if (!accountPagination.nextToken) return;
    loadAccounts(accountPagination.nextToken, [...accountPagination.previousTokens, accountPagination.currentToken]);
  };

  const goToPreviousAccountsPage = () => {
    const previousTokens = [...accountPagination.previousTokens];
    const previousToken = previousTokens.pop();
    loadAccounts(previousToken, previousTokens);
  };

  const goToNextReportsPage = () => {
    if (!reportPagination.nextToken) return;
    loadReports(reportPagination.nextToken, [...reportPagination.previousTokens, reportPagination.currentToken]);
  };

  const goToReportsPage = (page: number) => {
    const currentPage = reportPagination.previousTokens.length + 1;
    if (page === currentPage) return;
    if (page === currentPage + 1 && reportPagination.nextToken) {
      goToNextReportsPage();
      return;
    }
    if (page < currentPage) {
      const token = page === 1 ? undefined : reportPagination.previousTokens[page - 1];
      loadReports(token, reportPagination.previousTokens.slice(0, page - 1));
    }
  };

  const handleRefreshAccount = async (account: CloudAccount, scope: 'quick' | 'inventory' | 'full' = 'quick') => {
    if (!['aws', 'azure', 'gcp'].includes(account.provider)) {
      message.warning('Atualizacao automatica ainda nao disponivel para este provider.');
      return;
    }

    setRefreshingAccountId(account.id);

    try {
      const response = await apiClient.post(`/cloud/accounts/${account.id}/refresh`, { scope });

      if (account.provider === 'gcp' && response.status === 202) {
        message.info(scope === 'quick'
          ? 'Atualização financeira GCP iniciada em segundo plano.'
          : 'Coleta GCP iniciada. Você pode continuar usando a plataforma; o relatório aparecerá quando estiver pronto.');
        await loadData();
        return;
      }

      const nextAccount = response.data?.account || account;
      const nextData = {
        ...(response.data?.data || {}),
        reportId: response.data?.report?.id,
        cloudAccountId: nextAccount.id,
        cloudAccount: nextAccount
      };

      if (account.provider === 'aws') {
        if (!hasMeaningfulAwsDashboardData(nextData)) {
          message.warning('A atualização da AWS retornou sem custos, inventário ou recomendações. Mantive os dados anteriores e não abri o dashboard vazio.');
          await loadData();
          return;
        }

        localStorage.setItem('awsData', JSON.stringify(nextData));
        localStorage.setItem('awsConnected', 'true');
        localStorage.setItem('awsCloudAccountId', nextAccount.id);
        setConnection('aws', {
          provider: 'aws',
          connected: true,
          cloudAccountId: nextAccount.id,
          accountId: nextAccount.accountId || nextAccount.providerAccountId
        });

        message.success(scope === 'quick' ? 'Custos atualizados. Abrindo dashboard AWS.' : 'Dados atualizados. Abrindo dashboard AWS.');
        await loadData();
        const reportId = response.data?.report?.id || nextData.reportId;
        navigate(
          `/aws-dashboard?cloudAccountId=${encodeURIComponent(nextAccount.id)}` +
          (reportId ? `&reportId=${encodeURIComponent(reportId)}` : '')
        );
        return;
      }

      if (account.provider === 'azure') {
        const subscriptionId = nextAccount.subscriptionId || nextAccount.providerAccountId || nextData.subscription?.id;
        if (subscriptionId) {
          localStorage.setItem('selectedSubscription', subscriptionId);
        }
        localStorage.setItem('azureCloudAccountId', nextAccount.id);
        localStorage.setItem('reportSnapshot', JSON.stringify({
          provider: 'azure',
          reportId: response.data?.report?.id,
          createdAt: new Date().toISOString(),
          data: {
            ...nextData,
            provider: 'azure',
            cloudAccountId: nextAccount.id
          }
        }));
        setConnection('azure', {
          provider: 'azure',
          connected: true,
          cloudAccountId: nextAccount.id,
          subscriptionId,
          accountId: subscriptionId
        });

        message.success(scope === 'quick' ? 'Custos atualizados. Abrindo dashboard Azure.' : 'Dados atualizados. Abrindo dashboard Azure.');
        await loadData();
        navigate('/');
        return;
      }

      if (account.provider === 'gcp') {
        localStorage.setItem('gcpData', JSON.stringify({
          ...nextData,
          provider: 'gcp',
          cloudAccountId: nextAccount.id
        }));
        localStorage.setItem('gcpCloudAccountId', nextAccount.id);
        localStorage.setItem('gcpProjectId', nextAccount.accountId || nextAccount.providerAccountId || nextData.account?.id || '');
        setConnection('gcp', {
          provider: 'gcp',
          connected: true,
          cloudAccountId: nextAccount.id,
          accountId: nextAccount.accountId || nextAccount.providerAccountId || nextData.account?.id
        });

        if (nextData.inventoryCoverage?.isPartial) {
          const failures = nextData.inventoryCoverage?.failedProjects?.length || 0;
          message.warning(`Inventário GCP parcial: ${failures} projeto(s) não puderam ser coletados.`);
        } else {
          message.success('Inventário e análise GCP atualizados.');
        }
        await loadData();
        navigate('/gcp-dashboard');
        return;
      }

      await loadData();
    } catch (refreshError: any) {
      console.error('Erro ao atualizar conta:', refreshError);
      message.error(refreshError.response?.data?.details || refreshError.response?.data?.error || 'Nao foi possivel atualizar a conta.');
    } finally {
      setRefreshingAccountId(null);
    }
  };

  const handleExportInventory = async (account: CloudAccount) => {
    setExportingInventoryAccountId(account.id);
    try {
      const response = await apiClient.get(`/cloud/accounts/${account.id}/inventory.csv`, {
        responseType: 'blob'
      });
      const disposition = String(response.headers['content-disposition'] || '');
      const filenameMatch = disposition.match(/filename="?([^";]+)"?/i);
      const filename = filenameMatch?.[1] || `inventario-${account.provider}-${new Date().toISOString().slice(0, 10)}.csv`;
      const url = URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
      const inventoryStatus = String(response.headers['x-inventory-status'] || '');
      if (inventoryStatus === 'partial') {
        message.warning('CSV exportado, mas o inventário está parcial. Consulte as colunas de cobertura e erros.');
      } else {
        message.success('Inventário GCP exportado em CSV.');
      }
    } catch (exportError: any) {
      console.error('Erro ao exportar inventário:', exportError);
      let details = exportError.response?.data?.details || exportError.response?.data?.error;
      if (exportError.response?.data instanceof Blob) {
        try {
          const payload = JSON.parse(await exportError.response.data.text());
          details = payload.details || payload.error;
        } catch {
          // Mantém a mensagem padrão quando a resposta não for JSON.
        }
      }
      message.error(details || 'Não foi possível exportar o inventário GCP. Atualize a conexão e tente novamente.');
    } finally {
      setExportingInventoryAccountId(null);
    }
  };

  const openEditAccount = (account: CloudAccount) => {
    const storedCustomerName = account.metadata?.customerName || '';
    const editableCustomerName = /^gcp\s+(organization|folder|project)\b/i.test(storedCustomerName)
      ? ''
      : storedCustomerName;
    setEditingAccount(account);
    setEditForm({
      name: account.name || getAccountDisplayName(account),
      region: account.region || '',
      customerName: editableCustomerName,
      customerDomain: account.metadata?.customerDomain || '',
      billingAccountId: account.metadata?.billingAccountId || '',
      billingExportProjectId: account.metadata?.billingExportProjectId || '',
      billingExportDataset: account.metadata?.billingExportDataset || '',
      billingExportTable: account.metadata?.billingExportTable || '',
      billingCorrectionPercent: account.metadata?.billingCorrectionPercent || ''
    });

    if (account.provider === 'gcp' && isPlatformAdmin) {
      setGcpTenantCorrectionPercent('');
      setLoadingGcpCorrectionPercent(true);
      apiClient.get('/gcp-settings/correction-percent')
        .then(response => setGcpTenantCorrectionPercent(response.data?.billingCorrectionPercent || ''))
        .catch(fetchError => {
          console.error('Erro ao carregar correção de faturamento GCP:', fetchError);
        })
        .finally(() => setLoadingGcpCorrectionPercent(false));
    }
  };

  const updateEditForm = (key: keyof typeof editForm, value: string) => {
    setEditForm(current => ({ ...current, [key]: value }));
  };

  const metadataValue = (value: string) => {
    return value.trim();
  };

  const isGenericGcpName = (value?: string) => /^gcp\s+(organization|folder|project)\b/i.test(String(value || '').trim());

  const customerNameFromDomain = (domain?: string) => {
    const firstLabel = String(domain || '')
      .trim()
      .toLowerCase()
      .replace(/^https?:\/\//, '')
      .replace(/^www\./, '')
      .split('/')[0]
      .split('.')[0];

    return firstLabel
      .split(/[-_]/)
      .filter(Boolean)
      .map(part => part.charAt(0).toUpperCase() + part.slice(1))
      .join(' ');
  };

  const handleSaveAccount = async () => {
    if (!editingAccount) return;
    setSavingAccountId(editingAccount.id);

    try {
      await apiClient.patch(`/cloud/accounts/${editingAccount.id}`, {
        name: editForm.name.trim(),
        region: editForm.region.trim(),
        metadata: {
          customerName: metadataValue(editForm.customerName),
          customerDomain: metadataValue(editForm.customerDomain),
          ...(editingAccount.provider === 'gcp'
            ? {
              billingAccountId: metadataValue(editForm.billingAccountId),
              billingExportProjectId: metadataValue(editForm.billingExportProjectId),
              billingExportDataset: metadataValue(editForm.billingExportDataset),
              billingExportTable: metadataValue(editForm.billingExportTable),
              ...(isPlatformAdmin ? { billingCorrectionPercent: metadataValue(editForm.billingCorrectionPercent) } : {})
            }
            : {})
        }
      });

      if (editingAccount.provider === 'gcp' && isPlatformAdmin) {
        setSavingGcpCorrectionPercent(true);
        try {
          await apiClient.put('/gcp-settings/correction-percent', {
            billingCorrectionPercent: metadataValue(gcpTenantCorrectionPercent)
          });
        } finally {
          setSavingGcpCorrectionPercent(false);
        }
      }

      message.success('Conexão atualizada.');
      setEditingAccount(null);
      await loadData();
    } catch (saveError: any) {
      console.error('Erro ao editar conta:', saveError);
      message.error(saveError.response?.data?.details || saveError.response?.data?.error || 'Nao foi possivel atualizar a conexão.');
    } finally {
      setSavingAccountId(null);
    }
  };

  const handleDeleteAccount = async (account: CloudAccount) => {
    setDeletingAccountId(account.id);

    try {
      await apiClient.delete(`/cloud/accounts/${account.id}`);
      message.success('Monitoramento removido. Relatórios históricos foram preservados.');
      await loadData();
    } catch (deleteError: any) {
      console.error('Erro ao remover conta:', deleteError);
      message.error(deleteError.response?.data?.details || deleteError.response?.data?.error || 'Nao foi possivel remover o monitoramento.');
    } finally {
      setDeletingAccountId(null);
    }
  };

  const handleOpenReportDashboard = async (report: ReportRun) => {
    setOpeningReportId(report.id);

    try {
      const response = await apiClient.get(`/reports/runs/${report.id}`, {
        params: { includeInventory: false }
      });
      const reportDetails = response.data?.report || report;
      const snapshot = reportDetails.snapshot;

      if (!snapshot) {
        message.warning('Este relatório foi gerado antes do armazenamento de snapshot. Para evitar recalcular, não abrimos um novo dashboard a partir dele.');
        return;
      }

      const provider = reportDetails.provider || snapshot.provider;
      const connectedAccount = reportDetails.cloudAccountId
        ? accountById[reportDetails.cloudAccountId]
        : undefined;
      const brandedSnapshot = {
        ...snapshot,
        customerName: connectedAccount?.metadata?.customerName || snapshot.customerName,
        customerDomain: connectedAccount?.metadata?.customerDomain || snapshot.customerDomain,
        customerLogoUrl: connectedAccount?.metadata?.customerLogoUrl || snapshot.customerLogoUrl
      };
      localStorage.setItem('reportSnapshot', JSON.stringify({
        provider,
        reportId: reportDetails.id,
        createdAt: reportDetails.createdAt,
        data: {
          ...brandedSnapshot,
          provider,
          reportId: reportDetails.id,
          cloudAccountId: reportDetails.cloudAccountId || snapshot.cloudAccountId
        }
      }));

      if (provider === 'aws') {
        setConnection('aws', {
          provider: 'aws',
          connected: true,
          cloudAccountId: reportDetails.cloudAccountId,
          accountId: reportDetails.accountId || snapshot.account?.id
        });
        navigate(
          `/aws-dashboard?cloudAccountId=${encodeURIComponent(
            reportDetails.cloudAccountId || snapshot.cloudAccountId || ''
          )}&reportId=${encodeURIComponent(reportDetails.id)}`
        );
        return;
      }

      if (provider === 'azure') {
        const subscriptionId = reportDetails.subscriptionId || snapshot.subscription?.id;
        if (subscriptionId) {
          localStorage.setItem('selectedSubscription', subscriptionId);
        }
        setConnection('azure', {
          provider: 'azure',
          connected: true,
          cloudAccountId: reportDetails.cloudAccountId,
          subscriptionId,
          accountId: subscriptionId
        });
        navigate('/');
        return;
      }

      if (provider === 'gcp') {
        const projectId = reportDetails.accountId || snapshot.account?.id || snapshot.scope?.id;
        const cloudAccountId = reportDetails.cloudAccountId || snapshot.cloudAccountId || '';
        localStorage.setItem('gcpData', JSON.stringify({
          ...brandedSnapshot,
          provider: 'gcp',
          reportId: reportDetails.id,
          cloudAccountId
        }));
        setConnection('gcp', {
          provider: 'gcp',
          connected: true,
          cloudAccountId,
          accountId: projectId
        });
        navigate(
          `/gcp-dashboard?cloudAccountId=${encodeURIComponent(cloudAccountId)}` +
          `&reportId=${encodeURIComponent(reportDetails.id)}`
        );
        return;
      }

      message.warning('Provider do relatório não suportado para abertura de dashboard.');
    } catch (openError: any) {
      console.error('Erro ao abrir dashboard do relatório:', openError);
      message.error(openError.response?.data?.details || 'Nao foi possivel abrir o dashboard deste relatório.');
    } finally {
      setOpeningReportId(null);
    }
  };

  const handleOpenLatestAccountDashboard = (account: CloudAccount) => {
    const latestLoadedReport = reports
      .filter(report => report.cloudAccountId === account.id)
      .sort((a, b) => String(b.createdAt || '').localeCompare(String(a.createdAt || '')))[0];
    const report = account.lastReportRunId
      ? {
          id: account.lastReportRunId,
          tenantId: account.tenantId,
          cloudAccountId: account.id,
          provider: account.provider,
          accountId: account.providerAccountId || account.accountId || account.subscriptionId
        }
      : latestLoadedReport;

    if (!report) {
      message.info('Esta conexão ainda não possui dashboard gerado. Clique em Atualizar para criar o primeiro relatório.');
      return;
    }

    handleOpenReportDashboard(report);
  };

  const reportCountByAccount = useMemo(() => {
    return reports.reduce<Record<string, number>>((acc, report) => {
      if (report.cloudAccountId) {
        acc[report.cloudAccountId] = (acc[report.cloudAccountId] || 0) + 1;
      }
      return acc;
    }, {});
  }, [reports]);

  const accountById = useMemo(() => {
    return accounts.reduce<Record<string, CloudAccount>>((acc, account) => {
      acc[account.id] = account;
      return acc;
    }, {});
  }, [accounts]);

  const getAccountDisplayName = useCallback((account?: CloudAccount) => {
    if (!account) return '';
    return (
      account.name ||
      account.metadata?.accountDisplayName ||
      account.metadata?.accountAlias ||
      account.providerAccountId ||
      account.accountId ||
      account.subscriptionId ||
      account.id
    );
  }, []);

  const customerAccountGroups = useMemo<CustomerAccountGroup[]>(() => {
    const groups = new Map<string, CustomerAccountGroup>();

    accounts.forEach(account => {
      const domain = normalizeCustomerDomain(account.metadata?.customerDomain);
      const configuredCustomerName = String(account.metadata?.customerName || '').trim();
      const customerName = configuredCustomerName && !isGenericGcpName(configuredCustomerName)
        ? configuredCustomerName
        : customerNameFromDomain(domain) || getAccountDisplayName(account);
      const normalizedCustomerName = customerName
        .trim()
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/\s+/g, ' ');
      const groupKey = domain
        ? `domain:${domain}`
        : configuredCustomerName
          ? `name:${configuredCustomerName.toLowerCase()}`
          : normalizedCustomerName
            ? `display:${normalizedCustomerName}`
            : `account:${account.id}`;
      const existing = groups.get(groupKey);
      const monthlyCost = Number(
        account.lastReportSummary?.monthlyAverageCost ??
        account.metadata?.lastMonthlyAverageCost ??
        0
      );

      if (!existing) {
        groups.set(groupKey, {
          id: groupKey,
          customerName,
          customerDomain: domain || undefined,
          accounts: [account],
          providers: [account.provider],
          latestUpdate: account.updatedAt,
          monthlyAverageCost: monthlyCost
        });
        return;
      }

      existing.accounts.push(account);
      existing.providers = Array.from(new Set([...existing.providers, account.provider]));
      existing.monthlyAverageCost += monthlyCost;
      if (String(account.updatedAt || '').localeCompare(String(existing.latestUpdate || '')) > 0) {
        existing.latestUpdate = account.updatedAt;
      }
    });

    return Array.from(groups.values()).sort((a, b) =>
      a.customerName.localeCompare(b.customerName, 'pt-BR')
    );
  }, [accounts, getAccountDisplayName]);

  const getAccountIdentifier = useCallback((account?: CloudAccount) => {
    if (!account) return '';
    return account.providerAccountId || account.accountId || account.subscriptionId || account.id;
  }, []);

  const getReportAccountDisplay = useCallback((report: ReportRun) => {
    const account = report.cloudAccountId ? accountById[report.cloudAccountId] : undefined;
    const displayName =
      getAccountDisplayName(account) ||
      report.summary?.accountName ||
      report.summary?.accountAlias ||
      report.summary?.customerLabel ||
      report.accountId ||
      report.cloudAccountId ||
      '-';
    const identifier =
      report.summary?.providerAccountId ||
      report.accountId ||
      report.summary?.scopeLabel ||
      getAccountIdentifier(account) ||
      report.cloudAccountId;

    return { displayName, identifier };
  }, [accountById, getAccountDisplayName, getAccountIdentifier]);

  const reportGroups = useMemo<ReportAccountGroup[]>(() => {
    const grouped = new Map<string, ReportAccountGroup>();
    reports.forEach(report => {
      const account = report.cloudAccountId ? accountById[report.cloudAccountId] : undefined;
      const { displayName, identifier } = getReportAccountDisplay(report);
      const groupId =
        report.cloudAccountId ||
        `${report.provider || account?.provider || 'cloud'}:${report.accountId || report.summary?.providerAccountId || identifier || displayName}`;
      const existing = grouped.get(groupId);
      const reportsForAccount = [...(existing?.reports || []), report]
        .sort((left, right) => String(right.createdAt || '').localeCompare(String(left.createdAt || '')));
      const latestReport = reportsForAccount[0];
      grouped.set(groupId, {
        id: groupId,
        displayName,
        identifier,
        provider: latestReport.provider || account?.provider,
        customerDomain: normalizeCustomerDomain(
          latestReport.summary?.customerDomain || account?.metadata?.customerDomain
        ) || undefined,
        customerLogoUrl: latestReport.summary?.customerLogoUrl || account?.metadata?.customerLogoUrl,
        latestReport,
        latestReportDate: latestReport.createdAt,
        reportCount: reportsForAccount.length,
        reports: reportsForAccount,
        monthlyAverageCost: latestReport.summary?.monthlyAverageCost,
        netMonthlySavings: latestReport.summary?.netMonthlySavings,
        createdByEmail: latestReport.createdByEmail || account?.createdByEmail
      });
    });
    return Array.from(grouped.values())
      .sort((left, right) => String(right.latestReportDate || '').localeCompare(String(left.latestReportDate || '')));
  }, [reports, accountById, getReportAccountDisplay]);

  const accountColumns: ColumnsType<CloudAccount> = [
    {
      title: 'Conta',
      dataIndex: 'name',
      key: 'name',
      render: (_, account) => {
        const displayName = getAccountDisplayName(account);
        const identifier = getAccountIdentifier(account);
        const logoCandidates = customerLogoCandidates(
          account.metadata?.customerDomain,
          account.metadata?.customerLogoUrl
        );
        const logoIndex = logoIndexByAccount[account.id] || 0;
        const logoUrl = logoCandidates[logoIndex];

        return (
          <div className="connected-accounts__account-cell">
            {logoUrl ? (
              <span className="connected-accounts__logo">
                <img
                  src={logoUrl}
                  alt={`Logo ${displayName}`}
                  onError={() => setLogoIndexByAccount(current => ({
                    ...current,
                    [account.id]: logoIndex + 1
                  }))}
                />
              </span>
            ) : (
              <span className="connected-accounts__logo connected-accounts__logo--fallback">
                {account.metadata?.customerDomain || displayName
                  ? <span className="connected-accounts__logo-initials">{customerInitials(displayName, account.metadata?.customerDomain)}</span>
                  : <CloudOutlined />}
              </span>
            )}
            <div>
              <strong>{displayName}</strong>
              {identifier && identifier !== displayName && (
                <div className="connected-accounts__muted">{identifier}</div>
              )}
            </div>
          </div>
        );
      }
    },
    {
      title: 'Provider',
      dataIndex: 'provider',
      key: 'provider',
      render: (provider) => <Tag>{providerLabel(provider)}</Tag>
    },
    {
      title: 'Proprietário',
      dataIndex: 'createdByEmail',
      key: 'createdByEmail',
      render: (email) => email || '-'
    },
    {
      title: 'Região',
      dataIndex: 'region',
      key: 'region',
      render: (region, account) => (
        <div>
          <div>{account.provider === 'aws' ? `Preferencial: ${region || '-'}` : region || '-'}</div>
          {account.provider === 'aws' && Number(account.metadata?.enabledRegionCount || 0) > 0 && (
            <div className="connected-accounts__muted">
              {account.metadata?.enabledRegionCount} habilitadas · {account.metadata?.regionWithResourcesCount || 0} com recursos
            </div>
          )}
        </div>
      )
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status, account) => {
        const refreshStatus = account.metadata?.lastRefreshStatus;
        if (refreshStatus === 'processing') {
          const progress = account.metadata?.lastRefreshProgress;
          const percent = Math.max(0, Math.min(99, Number(progress?.percent || 0)));
          const projects = progress?.projectsTotal
            ? `${progress.projectsCompleted || 0} de ${progress.projectsTotal} projetos`
            : null;
          return (
            <div className="connected-accounts__collection-progress">
              <Tag color="processing">
                {account.metadata?.lastRefreshScope === 'quick' ? 'Atualizando custos' : 'Coletando inventário'}
              </Tag>
              <Progress percent={percent} size="small" status="active" showInfo={false} />
              <div className="connected-accounts__muted">{progress?.message || 'Preparando coleta'}</div>
              {projects && <div className="connected-accounts__muted">{projects}</div>}
              <div className="connected-accounts__muted">{formatElapsedTime(account.metadata?.lastRefreshStartedAt)}</div>
              <div className="connected-accounts__muted">Tempo restante varia conforme quotas e volume de recursos.</div>
            </div>
          );
        }
        if (refreshStatus === 'failed') return <Tag color="error">Falha na coleta</Tag>;
        return <Tag color={status === 'connected' ? 'green' : 'default'}>{status || 'connected'}</Tag>;
      }
    },
    {
      title: 'Última atualização',
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      render: (_, account) => (
        <div>
          <div>Custos: {dateFormatter(account.metadata?.costsUpdatedAt || account.metadata?.lastRefreshAt || account.updatedAt)}</div>
          <div className="connected-accounts__muted">
            Inventário: {
              account.metadata?.inventoryUpdatedAt || (!account.metadata?.lastRefreshScope && account.metadata?.lastRefreshAt)
                ? dateFormatter(account.metadata?.inventoryUpdatedAt || account.metadata?.lastRefreshAt)
                : 'ainda não coletado'
            }
          </div>
        </div>
      )
    },
    {
      title: 'Relatórios',
      key: 'reports',
      render: (_, account) => account.reportCount || account.metadata?.reportCount || reportCountByAccount[account.id] || 0
    },
    {
      title: 'Ações',
      key: 'actions',
      fixed: 'right',
      render: (_, account) => (
        <Space>
          {canManageCloudAccounts && (
            <Button
              icon={<EditOutlined />}
              onClick={() => openEditAccount(account)}
            >
              Editar
            </Button>
          )}
          {canGenerateReports && (
            <Dropdown.Button
              icon={<ReloadOutlined />}
              loading={refreshingAccountId === account.id}
              disabled={account.metadata?.lastRefreshStatus === 'processing'}
              onClick={() => handleRefreshAccount(account, 'quick')}
              menu={{
                items: [
                  { key: 'inventory', label: 'Atualizar inventário' },
                  { key: 'full', label: 'Atualização completa' }
                ],
                onClick: ({ key }) => handleRefreshAccount(account, key as 'inventory' | 'full')
              }}
            >
              Atualização rápida
            </Dropdown.Button>
          )}
          {['aws', 'azure', 'gcp'].includes(account.provider) && (
            <Button
              icon={<DownloadOutlined />}
              loading={exportingInventoryAccountId === account.id}
              onClick={() => handleExportInventory(account)}
            >
              Exportar inventário CSV
            </Button>
          )}
          {canManageCloudAccounts && (
            <Popconfirm
              title="Remover monitoramento?"
              description="A conexão sairá da lista, mas os relatórios históricos serão preservados."
              okText="Remover"
              cancelText="Cancelar"
              okButtonProps={{ danger: true }}
              onConfirm={() => handleDeleteAccount(account)}
            >
              <Button
                danger
                icon={<DeleteOutlined />}
                loading={deletingAccountId === account.id}
              >
                Remover
              </Button>
            </Popconfirm>
          )}
        </Space>
      )
    }
  ];

  const customerGroupColumns: ColumnsType<CustomerAccountGroup> = [
    {
      title: 'Cliente',
      dataIndex: 'customerName',
      key: 'customerName',
      render: (_, group) => {
        const logoCandidates = customerLogoCandidates(group.customerDomain);
        const logoIndex = logoIndexByAccount[group.id] || 0;
        const logoUrl = logoCandidates[logoIndex];

        return (
          <div className="connected-accounts__account-cell">
            {logoUrl ? (
              <span className="connected-accounts__logo">
                <img
                  src={logoUrl}
                  alt={`Ícone ${group.customerName}`}
                  onError={() => setLogoIndexByAccount(current => ({
                    ...current,
                    [group.id]: logoIndex + 1
                  }))}
                />
              </span>
            ) : (
              <span className="connected-accounts__logo connected-accounts__logo--fallback">
                <span className="connected-accounts__logo-initials">
                  {customerInitials(group.customerName, group.customerDomain)}
                </span>
              </span>
            )}
            <div>
              <strong>{group.customerName}</strong>
              {group.customerDomain && (
                <div className="connected-accounts__muted">{group.customerDomain}</div>
              )}
            </div>
          </div>
        );
      }
    },
    {
      title: 'Clouds',
      dataIndex: 'providers',
      key: 'providers',
      render: (providers: string[]) => (
        <Space wrap>{providers.map(provider => <Tag key={provider}>{providerLabel(provider)}</Tag>)}</Space>
      )
    },
    {
      title: 'Conexões',
      key: 'connections',
      render: (_, group) => group.accounts.length
    },
    {
      title: 'Última atualização',
      dataIndex: 'latestUpdate',
      key: 'latestUpdate',
      render: dateFormatter
    }
  ];

  const expandedReportColumns: ColumnsType<ReportRun> = [
    {
      title: 'Relatório',
      key: 'report',
      render: (_, report) => (
        <div>
          <strong>{report.reportType || 'dashboard-analysis'}</strong>
          <div className="connected-accounts__muted">{report.id}</div>
        </div>
      )
    },
    {
      title: 'Data',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: dateFormatter
    },
    {
      title: 'Custo médio',
      key: 'monthlyAverageCost',
      render: (_, report) => currencyFormatterForProvider(report.provider).format(Number(report.summary?.monthlyAverageCost || 0))
    },
    {
      title: 'Economia mensal',
      key: 'netMonthlySavings',
      render: (_, report) => currencyFormatterForProvider(report.provider).format(Number(report.summary?.netMonthlySavings || 0))
    },
    {
      title: 'Gerado por',
      dataIndex: 'createdByEmail',
      key: 'createdByEmail',
      render: (email) => email || '-'
    },
    {
      title: 'Ações',
      key: 'actions',
      fixed: 'right',
      render: (_, report) => (
        <Button
          size="small"
          icon={<EyeOutlined />}
          loading={openingReportId === report.id}
          onClick={() => handleOpenReportDashboard(report)}
        >
          Abrir dashboard
        </Button>
      )
    }
  ];

  const reportGroupColumns: ColumnsType<ReportAccountGroup> = [
    {
      title: 'Conta',
      key: 'account',
      render: (_, group) => {
        const logoCandidates = customerLogoCandidates(group.customerDomain, group.customerLogoUrl);
        const logoIndex = logoIndexByAccount[group.id] || 0;
        const logoUrl = logoCandidates[logoIndex];
        return (
          <div className="connected-accounts__account-cell">
            {logoUrl ? (
              <span className="connected-accounts__logo">
                <img
                  src={logoUrl}
                  alt={`Logo ${group.displayName}`}
                  onError={() => setLogoIndexByAccount(current => ({
                    ...current,
                    [group.id]: logoIndex + 1
                  }))}
                />
              </span>
            ) : (
              <span className="connected-accounts__logo connected-accounts__logo--fallback">
                <span className="connected-accounts__logo-initials">
                  {customerInitials(group.displayName, group.customerDomain)}
                </span>
              </span>
            )}
            <div>
              <strong>{group.displayName}</strong>
              {group.identifier && group.identifier !== group.displayName && (
                <div className="connected-accounts__muted">{group.identifier}</div>
              )}
            </div>
          </div>
        );
      }
    },
    {
      title: 'Provider',
      dataIndex: 'provider',
      key: 'provider',
      width: 100,
      render: provider => <Tag>{providerLabel(provider)}</Tag>
    },
    {
      title: 'Relatórios',
      dataIndex: 'reportCount',
      key: 'reportCount',
      width: 100
    },
    {
      title: 'Última geração',
      dataIndex: 'latestReportDate',
      key: 'latestReportDate',
      render: dateFormatter
    },
    {
      title: 'Custo médio',
      key: 'monthlyAverageCost',
      render: (_, group) =>
        currencyFormatterForProvider(group.provider).format(Number(group.monthlyAverageCost || 0))
    },
    {
      title: 'Economia mensal',
      key: 'netMonthlySavings',
      render: (_, group) =>
        currencyFormatterForProvider(group.provider).format(Number(group.netMonthlySavings || 0))
    },
    {
      title: 'Gerado por',
      dataIndex: 'createdByEmail',
      key: 'createdByEmail',
      render: email => email || '-'
    },
    {
      title: 'Ações',
      key: 'actions',
      fixed: 'right',
      render: (_, group) => (
        <Button
          size="small"
          icon={<EyeOutlined />}
          loading={openingReportId === group.latestReport.id}
          onClick={() => handleOpenReportDashboard(group.latestReport)}
        >
          Abrir último dashboard
        </Button>
      )
    }
  ];

  return (
    <div className="connected-accounts">
      <div className="connected-accounts__header">
        <div>
          <span className="connected-accounts__eyebrow">Operação FinOps</span>
          <h1>Contas conectadas e relatórios</h1>
          <p>Cada usuário vê somente suas próprias conexões e relatórios. Administradores possuem visão global.</p>
        </div>
        <Button icon={<ReloadOutlined />} onClick={loadData} loading={loading}>
          Recarregar
        </Button>
      </div>

      {error && <Alert type="error" showIcon message={error} style={{ marginBottom: 16 }} />}

      {canManageReportRecipients && <ReportEmailRecipientsCard />}

      <Card
        title={<span><CloudOutlined /> Contas conectadas</span>}
        className="connected-accounts__card connected-accounts__card--accounts"
        extra={
          <Space wrap>
            <Input.Search
              allowClear
              placeholder="Buscar conta, ID ou região"
              onSearch={(value) => updateAccountFilter('search', value)}
              style={{ width: 230 }}
            />
            <Select
              allowClear
              placeholder="Provider"
              value={accountFilters.provider}
              onChange={(value) => updateAccountFilter('provider', value)}
              style={{ width: 130 }}
              options={[
                { value: 'aws', label: 'AWS' },
                { value: 'azure', label: 'Azure' },
                { value: 'gcp', label: 'GCP' }
              ]}
            />
            <Input
              allowClear
              placeholder="Proprietário"
              value={accountFilters.ownerEmail}
              onChange={(event) => updateAccountFilter('ownerEmail', event.target.value)}
              style={{ width: 210 }}
            />
            <Button onClick={() => loadAccounts(undefined, [])} loading={loadingAccounts}>
              Filtrar
            </Button>
          </Space>
        }
      >
        {loadingAccounts && !accounts.length ? (
          <div style={{ padding: '48px 0', textAlign: 'center' }}>
            <Spin size="large" />
          </div>
        ) : accounts.length ? (
          <>
            <Table
              rowKey="id"
              size="middle"
              loading={loadingAccounts}
              columns={customerGroupColumns}
              dataSource={customerAccountGroups}
              pagination={false}
              scroll={{ x: 900 }}
              expandable={{
                expandedRowRender: (group) => (
                  <div className="connected-accounts__expanded-reports">
                    <div className="connected-accounts__expanded-head">
                      <strong>Conexões de {group.customerName}</strong>
                      <span>{group.accounts.length} conta(s), subscription(s) ou projeto(s)</span>
                    </div>
                    <Table
                      rowKey="id"
                      size="small"
                      columns={accountColumns}
                      dataSource={group.accounts}
                      pagination={false}
                      scroll={{ x: 1400 }}
                      onRow={(account) => ({
                        onClick: (event) => {
                          const target = event.target as HTMLElement;
                          if (target.closest('button, a, .ant-dropdown, .ant-popover, .ant-select')) return;
                          handleOpenLatestAccountDashboard(account);
                        },
                        className: 'connected-accounts__clickable-row'
                      })}
                    />
                  </div>
                ),
                rowExpandable: (group) => group.accounts.length > 0
              }}
            />
            <div className="connected-accounts__pager">
              <span>{customerAccountGroups.length} cliente(s), contendo {accounts.length} conexão(ões) nesta página</span>
              <Space>
                <Button disabled={!accountPagination.previousTokens.length || loadingAccounts} onClick={goToPreviousAccountsPage}>
                  Anterior
                </Button>
                <Button disabled={!accountPagination.nextToken || loadingAccounts} onClick={goToNextAccountsPage}>
                  Próxima
                </Button>
              </Space>
            </div>
          </>
        ) : (
          <Empty description="Nenhuma conta conectada salva ainda." />
        )}
      </Card>

      <Card
        title={<span><FileTextOutlined /> Relatórios por conta</span>}
        className="connected-accounts__card connected-accounts__card--reports"
        extra={
          <Space wrap>
            <Input.Search
              allowClear
              placeholder="Buscar relatório, conta ou ID"
              onSearch={(value) => updateReportFilter('search', value)}
              style={{ width: 240 }}
            />
            <Select
              allowClear
              placeholder="Provider"
              value={reportFilters.provider}
              onChange={(value) => updateReportFilter('provider', value)}
              style={{ width: 130 }}
              options={[
                { value: 'aws', label: 'AWS' },
                { value: 'azure', label: 'Azure' },
                { value: 'gcp', label: 'GCP' }
              ]}
            />
            <Input
              allowClear
              placeholder="Proprietário"
              value={reportFilters.ownerEmail}
              onChange={(event) => updateReportFilter('ownerEmail', event.target.value)}
              style={{ width: 210 }}
            />
            <input
              className="connected-accounts__date-filter"
              type="date"
              value={reportFilters.dateFrom || ''}
              onChange={(event) => updateReportFilter('dateFrom', event.target.value)}
            />
            <input
              className="connected-accounts__date-filter"
              type="date"
              value={reportFilters.dateTo || ''}
              onChange={(event) => updateReportFilter('dateTo', event.target.value)}
            />
            <Button onClick={() => loadReports(undefined, [])} loading={loadingReports}>
              Filtrar
            </Button>
          </Space>
        }
      >
        {loadingReports && !reports.length ? (
          <div style={{ padding: '48px 0', textAlign: 'center' }}>
            <Spin size="large" />
          </div>
        ) : reports.length ? (
          <>
            <Table
              rowKey="id"
              size="middle"
              loading={loadingReports}
              columns={reportGroupColumns}
              dataSource={reportGroups}
              pagination={false}
              expandable={{
                expandedRowRender: group => (
                  <div className="connected-accounts__expanded-reports">
                    <div className="connected-accounts__expanded-head">
                      <strong>Histórico de {group.displayName}</strong>
                      <span>{group.reports.length} relatório(s) carregado(s) nesta página</span>
                    </div>
                    <Table
                      rowKey="id"
                      size="small"
                      columns={expandedReportColumns}
                      dataSource={group.reports}
                      pagination={false}
                      scroll={{ x: 1300 }}
                    />
                  </div>
                ),
                rowExpandable: group => group.reports.length > 0
              }}
              scroll={{ x: 1400 }}
            />
            <div className="connected-accounts__pager">
              <span>
                {reportGroups.length} conta(s), contendo {reports.length} relatório(s) carregado(s) nesta página
              </span>
              <Pagination
                current={reportPagination.previousTokens.length + 1}
                pageSize={REPORT_PAGE_SIZE}
                total={(
                  reportPagination.previousTokens.length + 1 + (reportPagination.nextToken ? 1 : 0)
                ) * REPORT_PAGE_SIZE}
                showSizeChanger={false}
                disabled={loadingReports}
                onChange={goToReportsPage}
                showLessItems
              />
            </div>
          </>
        ) : (
          <Empty description="Nenhuma análise ou exportação registrada ainda para as contas conectadas." />
        )}
      </Card>

      <Modal
        title="Editar conexão"
        open={Boolean(editingAccount)}
        onCancel={() => setEditingAccount(null)}
        onOk={handleSaveAccount}
        okText="Salvar"
        cancelText="Cancelar"
        confirmLoading={Boolean(savingAccountId)}
        destroyOnClose
      >
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <Alert
            type="info"
            showIcon
            message="Depois de salvar, clique em Atualizar para gerar um novo dashboard com os dados alterados."
          />
          <div>
            <label className="connected-accounts__field-label">Nome da conexão</label>
            <Input
              value={editForm.name}
              onChange={(event) => updateEditForm('name', event.target.value)}
              placeholder="Nome para identificação"
            />
          </div>
          <div>
            <label className="connected-accounts__field-label">
              {editingAccount?.provider === 'aws' ? 'Região preferencial' : 'Região'}
            </label>
            <Input
              value={editForm.region}
              onChange={(event) => updateEditForm('region', event.target.value)}
              placeholder="us-east-1, us-central1..."
            />
          </div>

          <div>
            <label className="connected-accounts__field-label">Nome do cliente</label>
            <Input
              value={editForm.customerName}
              onChange={(event) => updateEditForm('customerName', event.target.value)}
              placeholder="Nome da empresa"
            />
          </div>
          <div>
            <label className="connected-accounts__field-label">Domínio do cliente</label>
            <Input
              value={editForm.customerDomain}
              onChange={(event) => updateEditForm('customerDomain', event.target.value)}
              placeholder="empresa.com.br"
            />
            <div className="connected-accounts__muted">
              Usado para carregar o favicon e agrupar todas as conexões deste cliente.
            </div>
          </div>

          {editingAccount?.provider === 'gcp' && (
            <>
              <Alert
                type="warning"
                showIcon
                message="Para exibir custo GCP, informe o Cloud Billing Export para BigQuery."
                description="Sem esses campos, a xKnow continua exibindo inventário e segurança, mas os cards financeiros ficam zerados."
              />
              <div>
                <label className="connected-accounts__field-label">Billing Account ID</label>
                <Input
                  value={editForm.billingAccountId}
                  onChange={(event) => updateEditForm('billingAccountId', event.target.value)}
                  placeholder="016AA5-A7B042-F8F96B"
                />
              </div>
              <div>
                <label className="connected-accounts__field-label">Projeto do Billing Export</label>
                <Input
                  value={editForm.billingExportProjectId}
                  onChange={(event) => updateEditForm('billingExportProjectId', event.target.value)}
                  placeholder="billing-export-project"
                />
              </div>
              <div>
                <label className="connected-accounts__field-label">Dataset</label>
                <Input
                  value={editForm.billingExportDataset}
                  onChange={(event) => updateEditForm('billingExportDataset', event.target.value)}
                  placeholder="billing_export"
                />
              </div>
              <div>
                <label className="connected-accounts__field-label">Tabela</label>
                <Input
                  value={editForm.billingExportTable}
                  onChange={(event) => updateEditForm('billingExportTable', event.target.value)}
                  placeholder="gcp_billing_export_v1_XXXXXX"
                />
              </div>
              {isPlatformAdmin && (
                <div>
                  <label className="connected-accounts__field-label">Correção de faturamento (%) — padrão global (todas as contas GCP)</label>
                  <InputNumber
                    style={{ width: '100%' }}
                    value={gcpTenantCorrectionPercent === '' ? null : Number(gcpTenantCorrectionPercent)}
                    onChange={(value) => setGcpTenantCorrectionPercent(value === null ? '' : String(value))}
                    step={0.1}
                    placeholder="Ex.: 10.9 (deixe em branco se não aplicável)"
                    addonAfter="%"
                    disabled={loadingGcpCorrectionPercent || savingGcpCorrectionPercent}
                  />
                  <div className="connected-accounts__field-hint">
                    Visível só para platform-admin. Valor padrão aplicado a todas as contas GCP do tenant, ajustando o
                    custo bruto do Billing Export pra aproximar o que um revendedor/distribuidor (ex.: TD Synnex)
                    realmente fatura. Passa a valer nos próximos relatórios gerados a partir de agora — relatórios já
                    existentes não são recalculados. Nunca aparece pro cliente final.
                  </div>
                </div>
              )}
              {isPlatformAdmin && (
                <div>
                  <label className="connected-accounts__field-label">Correção de faturamento (%) — só esta conta</label>
                  <InputNumber
                    style={{ width: '100%' }}
                    value={editForm.billingCorrectionPercent === '' ? null : Number(editForm.billingCorrectionPercent)}
                    onChange={(value) => updateEditForm('billingCorrectionPercent', value === null ? '' : String(value))}
                    step={0.1}
                    placeholder={
                      gcpTenantCorrectionPercent === ''
                        ? 'Deixe em branco pra usar o padrão global (nenhum configurado)'
                        : `Deixe em branco pra usar o padrão global (${gcpTenantCorrectionPercent}%)`
                    }
                    addonAfter="%"
                  />
                  <div className="connected-accounts__field-hint">
                    Sobrescreve o padrão global só pra esta conta — útil se este cliente específico tiver uma margem de
                    revenda diferente. Em branco, usa o padrão global acima.
                  </div>
                </div>
              )}
            </>
          )}
        </Space>
      </Modal>
    </div>
  );
};

export default ConnectedAccounts;
